import React, { useLayoutEffect, useState, useEffect } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { startCase, toLower } from "lodash-es";
import useChart from "../../../../Components/Chart/useChart";
import Chart from "../../../../Components/Chart/Chart";
import { ticketStatusEnum } from "@SeemplicityDev/shared";
import useAppViewNavigator from "../../../../AppView/useAppViewNavigator";
import useSummary from "../../../useSummary";
import { getFilterJson } from "../../../../AppView/AppViewUtils";
import { statusCategories } from "../../../../Findings/FindingDetailsPane/FindingDetailsPaneHeader/Statuses/StatusUtils";
import type { FilterPanelsData } from "../../../../AppView/AppViewUtils.types";

interface FindingsByTicketStatusProps {
  selectedScope?: { value?: { id?: string } } | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
}

/**
 * A single row of the summary response: [category, status, findingsAmount].
 * The summary endpoint returns parsed-JSON tuples, so the runtime shape is a
 * fixed-position array rather than a keyed object.
 */
type SummaryRow = [string | null, string, number];

interface StatusDatum {
  category: string;
  from: number;
  to: number;
  findingsAmount: number;
  name: string;
  fill: string;
}

// The amcharts entry in ticketStatusEnum keyed by status type.
interface TicketStatusEntry {
  text: string;
  icon: string;
  value: string;
}

const getTicketColor = (type: string): string => {
  switch (type) {
    case "BACKLOG":
      return "#8DC9F3";
    case "SCHEDULED":
      return "#5565f9";
    case "IN_PROGRESS":
      return "#A673F1";
    case "REJECTED":
      return "#ec2a3f";
    default:
      return "#2dbf9f";
  }
};

const FindingsByTicketStatus: React.FC<FindingsByTicketStatusProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
}) => {
  const {
    summaryData: data,
    summaryDataError,
    summaryLoading,
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.id"],
      group_by: ["finding.category", "ticket.status"],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: !scopeToExecute && !selectedScope?.value?.id,
  });
  const [dashboardData, setDashboardData] = useState<StatusDatum[] | null>(
    null
  );
  const [totalFindings, setTotalFindings] = useState(0);
  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart();
  const { appViewNavigator } = useAppViewNavigator();

  const createYAxis = (yAxis: am4charts.CategoryAxis): void => {
    yAxis.renderer.grid.template.disabled = true;
    yAxis.renderer.grid.template.location = 0;
    yAxis.renderer.minGridDistance = 10;
    yAxis.paddingBottom = 14;
    yAxis.renderer.labels.template.fill = am4core.color("#9494a2");
    yAxis.renderer.labels.template.maxWidth = 120;
    yAxis.renderer.labels.template.fontSize = "11px";
  };

  const createXAxis = (xAxis: am4charts.ValueAxis): void => {
    xAxis.max = totalFindings;
    xAxis.min = 0;
    xAxis.renderer.labels.template.fill = am4core.color("#9494a2");
    xAxis.renderer.labels.template.fontSize = 12;
  };

  const clearData = (): void => {
    setDashboardData(null);
  };

  useEffect(() => {
    if (!!summaryLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [summaryLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!summaryDataError) {
      setChartError(false);
      setDashboardData([]);
      stopLoadingChart();
    }
  }, [summaryDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      const summaryRows = data as SummaryRow[];
      const categories: string[] = [];
      const dataObj: Record<string, Record<string, number>> = {};

      for (let i = 0; i < summaryRows.length; i += 1) {
        const category = summaryRows[i][0] || "Missing Category";
        if (!categories.includes(category)) {
          categories.push(category);
          dataObj[category] = {};
        }

        const status = summaryRows[i][1];

        const findingsAmount = summaryRows[i][2];

        dataObj[category][status] = findingsAmount;
      }

      const dataArray: StatusDatum[] = [];
      let max = 0;

      for (const category in dataObj) {
        let total = Object.keys(dataObj[category]).reduce(
          (prev, next) => prev + dataObj[category][next],
          0
        );
        if (total > max) {
          max = total;
        }

        const statuses: StatusDatum[] = [];
        for (let i = 0; i < Object.keys(dataObj[category]).length; i += 1) {
          const statusType = Object.keys(dataObj[category])[i];
          const findingsAmount = dataObj[category][statusType];
          if (statusType === "DELETED") {
            total = total - findingsAmount;
          } else {
            const from = i === 0 ? 0 : statuses[i - 1].to;
            const to = from + findingsAmount;
            statuses[i] = {
              category:
                category !== "Missing Category"
                  ? category.toUpperCase()
                  : category,
              from,
              to,
              findingsAmount,
              name: startCase(toLower(statusType)),
              fill: getTicketColor(statusType),
            };
          }
        }
        dataArray.push(...statuses);
      }

      setTotalFindings(max);

      if (dataArray?.length === 0) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (dataArray?.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      setDashboardData(dataArray);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      let [chart, legendContainer] = createChart(
        "ticketStatusCategoryDashboard",
        am4charts.XYChart
      );
      chart.data = dashboardData ?? [];
      chart.paddingTop = 30;
      chart.paddingRight = 40;

      const legendKeys: string[] = [];
      const legendData: StatusDatum[] = [];
      dashboardData?.forEach((el) => {
        if (!legendKeys.includes(el.name)) {
          legendKeys.push(el.name);
          legendData.push(el);
        }
      });

      let yAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      createYAxis(yAxis);
      yAxis.dataFields.category = "category";
      let xAxis = chart.xAxes.push(new am4charts.ValueAxis());
      createXAxis(xAxis);

      let label = yAxis.renderer.labels.template;
      label.wrap = true;
      label.maxWidth = 200;
      label.textAlign = "middle";

      let series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueX = "to";
      series.dataFields.openValueX = "from";
      series.dataFields.categoryY = "category";
      series.columns.template.height = am4core.percent(80);
      series.columns.template.maxHeight = 40;
      series.columns.template.propertyFields.fill = "fill";
      series.columns.template.tooltipText =
        "[bold]{name}[/]\n[font-size:14px] {findingsAmount}";
      series.tooltip!.autoTextColor = false;
      series.columns.template.fillOpacity = 0.7;
      series.columns.template.propertyFields.stroke = "fill";
      series.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      series.columns.template.strokeOpacity = 1;
      series.columns.template.strokeWidth = 2;

      series.columns.template.adapter.add("stroke", () => {
        return am4core.color("#ffffff");
      });

      series.columns.template.events.on("hit", (e) => {
        let selectedStatus: string | undefined;
        const dataContext = e.target.dataItem?.dataContext as
          | StatusDatum
          | undefined;
        const ticketStatusMap = ticketStatusEnum as Record<
          string,
          TicketStatusEntry
        >;
        for (let key in ticketStatusMap) {
          if (ticketStatusMap[key].text === dataContext?.name) {
            selectedStatus = key;
            break;
          }
        }

        const filter: FilterPanelsData = {
          filterPanels: [
            {
              conditions: [
                {
                  field: "actual_status",
                  condition: "in",
                  value: [
                    {
                      type: "category",
                      key: statusCategories.OPEN,
                      id: statusCategories.OPEN,
                    },
                  ],
                },
                {
                  value: selectedStatus ? [selectedStatus] : [],
                  field: "ticket_status",
                  condition: "in",
                },
              ],
            },
          ],
        };

        appViewNavigator(null, null, getFilterJson(filter));
      });

      chart.legend.itemContainers.template.clickable = false;
      chart.legend.itemContainers.template.focusable = false;
      chart.legend.itemContainers.template.cursorOverStyle =
        am4core.MouseCursorStyle.default;
      chart.legend.useDefaultMarker = true;
      chart.legend.data = legendData;
      let markerTemplate = chart.legend.markers.template;
      markerTemplate.width = 12;
      markerTemplate.height = 12;
      markerTemplate.strokeOpacity = 0;

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [dashboardData, dataExists, createChart]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={"ticketStatusCategoryDashboard"}
      chartError={dataError}
      chartData={dataExists}
    />
  );
};

export default FindingsByTicketStatus;
